import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { useEffect } from "react";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { settings } from "@config/settings";
import { useTranslation } from "next-i18next";
import { MainRouter } from "@config/urls";
import { CountryLocationService } from "@services/country-location-service";
import { EntitiesFetcherService } from "@services/entities-fetcher";
import { localesParserDictionary } from "@utils/locales-parser";
import styles from "./misc-pages.module.scss";
import { useHasAcceptedCookie } from "@hooks/useHasAcceptedCookie";

const CookiesPage: NextPage = () => {
  const { t } = useTranslation();

  const [hasAcceptedThird, setHasAcceptedThird] = useHasAcceptedCookie(
    settings.COOKIES.THIRD_PARTY
  );
  const [hasAcceptedTechnical, setHasAcceptedTechnical] = useHasAcceptedCookie(
    settings.COOKIES.TECHNICAL
  );

  useEffect(() => {
    const delayFnNumChilds = setTimeout(() => {
      const number_of_element_in_div = document.getElementById(
        "cookie-declaration-container"
      )!.childElementCount;
      if (number_of_element_in_div < 2) {
        location.reload();
      }
    }, 1200);
    return () => clearTimeout(delayFnNumChilds);
  }, []);

  return (
    <>
      <MainLayout
        breadcrumb={[
          {
            label: t("urls.home"),
            url: MainRouter.home.generatePath(),
          },
          {
            label: t("urls.cookies"),
            url: MainRouter.cookies.generatePath(),
          },
        ]}
      >
        <div id="cookie-declaration-container" className={styles.root}>
          <script
            id="CookieDeclaration"
            src="https://consent.cookiebot.com/c5effecd-c2fe-49c4-9c00-f64d089580c4/cd.js"
            type="text/javascript"
            async
          ></script>

          {/**
            <Text as="h1">{t("CookiesPolicyView.title1")}</Text>
            <Text>{t("CookiesPolicyView.p1")}</Text>
            <Text as="h2">{t("CookiesPolicyView.title2")}</Text>
            <Text>{t("CookiesPolicyView.p2")}</Text>
            <Text as="h2">{t("CookiesPolicyView.title3")}</Text>
            <Text>{t("CookiesPolicyView.p3")}</Text>
            <ul>
              <li>{t("CookiesPolicyView.p4")}</li>
              <li>{t("CookiesPolicyView.p5")}</li>
            </ul>
            <Text>{t("CookiesPolicyView.p6")}</Text>
            <ul>
              <li>{t("CookiesPolicyView.p7")}</li>
              <li>{t("CookiesPolicyView.p8")}</li>
            </ul>
            <Text>{t("CookiesPolicyView.p9")}</Text>
            <Text>{t("CookiesPolicyView.p10")}</Text>
            <Text>{t("CookiesPolicyView.p11")}</Text>
            <Text>{t("CookiesPolicyView.p12")}</Text>
            <Text>{t("CookiesPolicyView.p13")}</Text>
            <Text>{t("CookiesPolicyView.p14")}</Text>
            <Text>{t("CookiesPolicyView.p15")}</Text>

            <Text as="h4">{t("CookiesPolicyView.table1.title")}</Text>
            <table>
              <thead>
                <tr>
                  <th>{t("CookiesPolicyView.table1.th1")}</th>
                  <th>{t("CookiesPolicyView.table1.th2")}</th>
                  <th>{t("CookiesPolicyView.table1.th3")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t("CookiesPolicyView.table1.tr1.td1")}</td>
                  <td>{t("CookiesPolicyView.table1.tr1.td2")}</td>
                  <td>{t("CookiesPolicyView.table1.tr1.td3")}</td>
                </tr>
              </tbody>
            </table>

            <Text as="h4">{t("CookiesPolicyView.table2.title")}</Text>
            <table>
              <thead>
                <tr>
                  <th>{t("CookiesPolicyView.table2.th1")}</th>
                  <th>{t("CookiesPolicyView.table2.th2")}</th>
                  <th>{t("CookiesPolicyView.table2.th3")}</th>
                  <th>{t("CookiesPolicyView.table2.th4")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t("CookiesPolicyView.table2.tr1.td1")}</td>
                  <td>{t("CookiesPolicyView.table2.tr1.td2")}</td>
                  <td>{t("CookiesPolicyView.table2.tr1.td3")}</td>
                  <td>
                    <a
                      style={{ color: "blue" }}
                      href="https://developers.google.com/analytics/devguides/collection/analyticsjs/cookie-usage"
                    >
                      {`Google Analytics `}
                    </a>
                    <br />
                    <a
                      style={{ color: "blue" }}
                      href="https://policies.google.com/privacy?hl=es"
                    >
                      {`Google Privacy`}
                    </a>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Facebook</td>
                  <td>{t("CookiesPolicyView.table2.tr1.td3")}</td>
                  <td>
                    <a
                      style={{ color: "blue" }}
                      href="https://www.facebook.com/business/help/471978536642445"
                    >
                      Facebook Pixel
                    </a>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td>Linkedin</td>
                  <td>{t("CookiesPolicyView.table2.tr1.td3")}</td>
                  <td>
                    <a
                      style={{ color: "blue" }}
                      href="https://es.linkedin.com/legal/l/cookie-table?"
                    >
                      Linkedin Analytics
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>

            <Text>{t("CookiesPolicyView.p16")}</Text>
            <Text>{t("CookiesPolicyView.p17")}</Text>

            <Text as="h2">{t("CookiesPolicyView.title4")}</Text>
            <Text>{t("CookiesPolicyView.p18")}</Text>
            <Text>{t("CookiesPolicyView.p19")}</Text>
            <ul>
              <li>
                <strong>Firefox: </strong>
                <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es">
                  https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es
                </a>
              </li>
              <li>
                <strong>Chrome: </strong>
                <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es">
                  https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es
                </a>
              </li>
              <li>
                <strong>Internet Explorer: </strong>
                <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es">
                  https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es
                </a>
              </li>
              <li>
                <strong>Microsoft Edge: </strong>
                <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es">
                  https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es
                </a>
              </li>
              <li>
                <strong>Safari: </strong>
                <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es">
                  https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es
                </a>
              </li>
              <li>
                <strong>Opera: </strong>
                <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es">
                  https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias?redirectslug=habilitar-y-deshabilitar-cookies-que-los-sitios-we&redirectlocale=es
                </a>
              </li>
              <li>{t("CookiesPolicyView.p20")}</li>
            </ul>
            <Text>{t("CookiesPolicyView.p21")}</Text>
            <Text>{t("CookiesPolicyView.p22")}</Text>
            <Text as="h2">{t("CookiesPolicyView.title5")}</Text>
            <div className={styles.slots}>
              <CookieSlot
                checked={hasAcceptedThird}
                onChange={() => {
                  setHasAcceptedThird(!hasAcceptedThird);
                  location.reload();
                }}
                label={t("CookiesPolicyView.configuration.thirdPartyCookies")}
              ></CookieSlot>
              <CookieSlot
                checked={hasAcceptedTechnical}
                onChange={() => {
                  setHasAcceptedTechnical(!hasAcceptedTechnical);
                  location.reload();
                }}
                label={t("CookiesPolicyView.configuration.technicalCookies")}
              ></CookieSlot>
            </div>
          */}
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, res } = Props;

  let selectedCountry =
    await new CountryLocationService().getSelectedCountryData(req, res);

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: selectedCountry.countrycode,
  };

  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    productosDestacados: true,
    productosGenericos: true,
  });
  return {
    props: {
      country: selectedCountry.countrycode,
      productosGenericos: JSON.stringify(entities.productosGenericosData),
      productosDestacadosData: JSON.stringify(entities.productosDestacadosData),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default CookiesPage;
